/**
 * Integration mode + assignment-source helpers.
 *
 * The wizard does NOT let the RN choose an agency — that's set upstream
 * by either:
 *   • the parent app (when the visit is deep-linked via `?visit_id=…`)
 *   • the Avida Care coordinator (when `?draft=…&source=avida_care`)
 *   • a preview-mode OrganizationPicker fallback (only when neither
 *     parent nor Avida secrets are configured server-side)
 *
 * `useIntegrationMode()` queries the backend once per session and caches
 * the result. `useAssignmentContext()` derives the active source from
 * the existing session/local storage hooks the wizard already maintains.
 */
import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_CACHE_KEY = "wi-pa-integration-mode-v1";

export function useIntegrationMode() {
  const [mode, setMode] = useState(() => {
    try {
      const cached = window.sessionStorage.getItem(SESSION_CACHE_KEY);
      if (cached) return JSON.parse(cached);
    } catch { /* non-fatal */ }
    return null;
  });

  useEffect(() => {
    if (mode) return;  // cache hit
    let cancelled = false;
    axios.get(`${API}/integration-mode`)
      .then((r) => {
        if (cancelled) return;
        setMode(r.data);
        try { window.sessionStorage.setItem(SESSION_CACHE_KEY, JSON.stringify(r.data)); }
        catch { /* non-fatal */ }
      })
      .catch(() => {
        // Fail-open to "preview" so the UI still renders if the
        // integration-mode endpoint is unreachable. Better to let the
        // RN see something than to hard-block on a transient outage.
        if (!cancelled) setMode({ mode: "preview", avida_configured: false, parent_configured: false });
      });
    return () => { cancelled = true; };
  }, [mode]);

  return mode;  // null while loading, then { mode, avida_configured, parent_configured }
}

/**
 * Inspects existing session/local storage to decide where the current
 * intake was assigned from. Returns one of:
 *   { source: "avida",   label: "Avida Care", agencyName: ... }
 *   { source: "parent",  label: "your agency", agencyName: ... }
 *   { source: "preview", label: "Preview · {OrgName}", agencyName: ... }
 *   { source: "none" }
 *
 * Read at render time only — does not subscribe. Pages that need to
 * react to a change should re-render when their own state updates
 * (which is what happens after the parent deep-link / Avida draft is
 * bootstrapped).
 */
export function readAssignmentContext() {
  try {
    const avidaDraftId = window.sessionStorage.getItem("wi-pa-avida-draft-id-v1");
    if (avidaDraftId) {
      return { source: "avida", label: "Avida Care", agencyName: "" };
    }
  } catch { /* non-fatal */ }
  try {
    const raw = window.localStorage.getItem("wi-pa-active-visit-v1");
    if (raw) {
      const v = JSON.parse(raw) || {};
      if (v.visit_id) {
        return {
          source: "parent",
          label: v.agency_name || "your agency",
          agencyName: v.agency_name || "",
        };
      }
    }
  } catch { /* non-fatal */ }
  try {
    const raw = window.localStorage.getItem("avida-selected-org-v1");
    if (raw) {
      const o = JSON.parse(raw) || {};
      if (o.id || o.name) {
        return {
          source: "preview",
          label: `Preview · ${o.name || "selected organization"}`,
          agencyName: o.name || "",
        };
      }
    }
  } catch { /* non-fatal */ }
  return { source: "none" };
}

/**
 * Hook variant — re-reads on mount + when storage events fire (e.g.,
 * the org picker writes its key, or the parent deep-link bootstrap
 * stores the visit). Pages that need live updates should use this.
 */
export function useAssignmentContext() {
  const [ctx, setCtx] = useState(() => readAssignmentContext());
  useEffect(() => {
    const refresh = () => setCtx(readAssignmentContext());
    window.addEventListener("storage", refresh);
    // Same-tab updates don't fire 'storage', so also re-read on focus.
    window.addEventListener("focus", refresh);
    return () => {
      window.removeEventListener("storage", refresh);
      window.removeEventListener("focus", refresh);
    };
  }, []);
  return ctx;
}
